import axios from 'axios';
import { userManager } from '../auth/oidcConfig';
import { getOperatorToken, setOperatorToken } from '../auth/betterAuthClient';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const getApiBaseUrl = () => {
  if (import.meta.env.DEV) return '/api';
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;
  throw new Error('VITE_API_URL environment variable is not configured');
};

export const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  transformRequest: [(data, headers) => {
    if (data instanceof FormData) delete headers['Content-Type'];
    return data;
  }, ...(axios.defaults.transformRequest as any[])],
});

api.interceptors.request.use(async (config: any) => {
  // Better Auth FIRST, Zitadel only as a fallback.
  //
  // Both have to work at once while the portal is mid-cutover, and the order
  // matters: an operator who has signed in through Better Auth may still be
  // holding a stale OIDC user in storage, and that stale token is the one that
  // would win if OIDC were checked first — sending the old identity on every
  // request of a session the user thinks is new.
  const operatorToken = getOperatorToken();
  if (operatorToken) {
    config.headers.Authorization = `Bearer ${operatorToken}`;
    return config;
  }

  try {
    const user = await userManager.getUser();
    if (user && !user.expired && user.access_token) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
  } catch (e: any) {
    console.warn('[Axios] Failed to retrieve OIDC token', e?.message || e);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: any) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const message = error.response?.data?.message || '';
    const looksExpired =
      status === 401 &&
      (message.includes('expired') || message.includes('Invalid token') || message === '');

    // A Better Auth session has no silent renew — there is no refresh token and
    // no IdP to renew against. Falling through to signinSilent() below would
    // send an operator who signed in with a password on a redirect to Zitadel,
    // which is the very thing this migration removes. Clear and re-sign-in.
    if (status === 401 && getOperatorToken()) {
      setOperatorToken(null);
      if (!location.pathname.startsWith('/signin')) {
        location.assign('/signin');
      }
      return Promise.reject(error);
    }

    if (looksExpired && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest._retry = true;
            if (token) originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshed = await userManager.signinSilent();
        const newToken = refreshed?.access_token ?? null;
        if (!newToken) throw new Error('Silent renew produced no token');
        processQueue(null, newToken);
        isRefreshing = false;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (e) {
        processQueue(new Error('Token refresh failed'), null);
        isRefreshing = false;
        // Full re-auth
        try {
          await userManager.signinRedirect();
        } catch {}
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
